import React, { useState } from "react"; 
import { useTranslation } from "react-i18next";
import { ShieldCheckIcon, DocumentTextIcon, ChevronLeftIcon } from '@heroicons/react/24/outline'; 

export default function Legal({ onClose, onOpenCookieConsent }) { 
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("privacy");

  return (
    <div className="fixed inset-0 bg-hover-light dark:bg-hover-dark bg-opacity-90 dark:bg-opacity-90 overflow-auto z-50 p-6" style={{ WebkitOverflowScrolling: "touch" }}>
      <div className="max-w-4xl mx-auto p-6 bg-background-light dark:bg-background-dark rounded-md border border-border-light dark:border-border-dark shadow-2xl">
        <div className="flex flex-col md:flex-row border-b border-border-light dark:border-border-dark mb-6">
          <button
            onClick={() => setActiveTab("privacy")}
            className={`flex items-center gap-2 py-2 px-4 font-semibold border-b-2 transition-colors duration-200 ${activeTab === "privacy"
                ? "border-bgHeader-light text-textActive-light dark:border-bgHeader-dark dark:text-textActive-dark bg-hover-light dark:bg-hover-dark"
                : "border-transparent text-text-light hover:text-textHover-light dark:text-text-dark dark:hover:text-textHover-dark"
              }`}
            aria-current={activeTab === "privacy" ? "page" : undefined}
            type="button"
          >
            <ShieldCheckIcon className="w-5 h-5" />
            {t("legal.tabs.privacyPolicy")}
          </button>

          <button
            onClick={() => setActiveTab("terms")}
            className={`flex items-center gap-2 py-2 px-4 font-semibold border-b-2 transition-colors duration-200 ${activeTab === "terms"
                ? "border-bgHeader-light text-textActive-light dark:border-bgHeader-dark dark:text-textActive-dark bg-hover-light dark:bg-hover-dark"
                : "border-transparent text-text-light hover:text-textHover-light dark:text-text-dark dark:hover:text-textHover-dark"
              }`}
            aria-current={activeTab === "terms" ? "page" : undefined}
            type="button"
          >
            <DocumentTextIcon className="w-5 h-5" />
            {t("legal.tabs.termsConditions")}
          </button>
        </div>

        <article className="prose prose-blue dark:prose-invert max-w-none text-base leading-relaxed">
          {activeTab === "privacy" && (
            <>
              <h1 className="text-lg font-bold mb-2">{t("legal.privacy.title")}</h1>
              <p className="mb-4">
                <strong>{t("legal.privacy.effectiveDate")}</strong> {t("legal.privacy.effectiveDateValue")}
              </p>
              <p className="mb-6">{t("legal.privacy.intro")}</p>

              <section>
                <h2 className="text-md font-semibold mb-2">{t("legal.privacy.sections.personalInfo.title")}</h2>
                <p className="mb-2">{t("legal.privacy.sections.personalInfo.description")}</p>
                <ul className="mb-4 list-disc list-inside">
                  {t("legal.privacy.sections.personalInfo.deviceInfo", { returnObjects: true }).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>

                <p className="mb-2">{t("legal.privacy.sections.personalInfo.formInfoIntro")}</p>
                <ul className="mb-4 list-disc list-inside">
                  {t("legal.privacy.sections.personalInfo.formInfoList", { returnObjects: true }).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>

                <p className="mb-6">{t("legal.privacy.sections.personalInfo.usage")}</p>
              </section>

              <section>
                <h2 className="text-md font-semibold mb-2">{t("legal.privacy.sections.usage.title")}</h2>
                <ul className="mb-6 list-disc list-inside">
                  {t("legal.privacy.sections.usage.list", { returnObjects: true }).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-md font-semibold mb-2">{t("legal.privacy.sections.ga4.title")}</h2>
                <p className="mb-2">{t("legal.privacy.sections.ga4.description")}</p>
                <p className="mb-2">
                  <a
                    href="https://policies.google.com/technologies/partner-sites"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                  >
                    {t("legal.privacy.sections.ga4.learnMore")}
                  </a>
                </p>
                <p className="mb-2">{t("legal.privacy.sections.ga4.optOutIntro")}</p>
                <ul className="mb-6 list-disc list-inside">
                  <li>
                    <a
                      href="https://tools.google.com/dlpage/gaoptout"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                    >
                      {t("legal.privacy.sections.ga4.optOutLink")}
                    </a>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-md font-semibold mb-2">{t("legal.privacy.sections.dataStorage.title")}</h2>
                <p className="mb-6">{t("legal.privacy.sections.dataStorage.description")}</p>
              </section>

              <section>
                <h2 className="text-md font-semibold mb-2">{t("legal.privacy.sections.rights.title")}</h2>
                <p className="mb-2">{t("legal.privacy.sections.rights.description")}</p>
                <ul className="mb-4 list-disc list-inside">
                  {t("legal.privacy.sections.rights.rightsList", { returnObjects: true }).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
                <p>
                  {t("legal.privacy.sections.rights.contactIntro")}{" "}
                  <a href="mailto:eduardomartinsw@outlook.com" className="underline text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"> 
                    eduardomartinsw [at] outlook [dot] com
                  </a>
                </p>
              </section>

              <section>
                <h2 className="text-md font-semibold mt-8 mb-2">{t("legal.privacy.sections.changes.title")}</h2>
                <p>{t("legal.privacy.sections.changes.description")}</p>
              </section>
            </>
          )}

          {activeTab === "terms" && (
            <>
              <h1 className="text-lg font-bold mb-2">{t("legal.terms.title")}</h1>
              <p className="mb-4">
                <strong>{t("legal.terms.effectiveDate")}</strong> {t("legal.terms.effectiveDateValue")}
              </p>
              <p className="mb-6">{t("legal.terms.intro")}</p>

              <section>
                <h2 className="text-md font-semibold mb-2">{t("legal.terms.sections.intellectualProperty.title")}</h2>
                <p className="mb-2">{t("legal.terms.sections.intellectualProperty.description")}</p>
                <ul className="mb-6 list-disc list-inside">
                  {t("legal.terms.sections.intellectualProperty.list", { returnObjects: true }).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-md font-semibold mb-2">{t("legal.terms.sections.fairUse.title")}</h2>
                <p className="mb-2">{t("legal.terms.sections.fairUse.description")}</p>
                {t("legal.terms.sections.fairUse.list", { returnObjects: true }).map((item, i) => (
                  <p className="mb-2" key={i}>{item}</p>
                ))}
              </section>

              <section>
                <h2 className="text-md font-semibold mb-2">{t("legal.terms.sections.restrictedAccess.title")}</h2>
                <p className="mb-6">{t("legal.terms.sections.restrictedAccess.description")}</p>
              </section>

              <section>
                <h2 className="text-md font-semibold mb-2">{t("legal.terms.sections.thirdParty.title")}</h2>
                <p className="mb-2">{t("legal.terms.sections.thirdParty.description")}</p>
                <ul className="mb-6 list-disc list-inside">
                  {t("legal.terms.sections.thirdParty.list", { returnObjects: true }).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-md font-semibold mb-2">{t("legal.terms.sections.liability.title")}</h2>
                <p className="mb-6">{t("legal.terms.sections.liability.description")}</p>
              </section>

              <section>
                <h2 className="text-md font-semibold mb-2">{t("legal.terms.sections.changes.title")}</h2>
                <p>{t("legal.terms.sections.changes.description")}</p>
              </section>
            </>
          )}
        </article>

        <div className="flex flex-col-reverse sm:flex-row justify-center sm:justify-end mt-6 mb-6 gap-4 m-6">
            <button
                className="px-4 py-2 rounded action-button" 
                onClick={onClose}
                aria-label={t("legal.buttons.back") || "Back to main site"}
            ><ChevronLeftIcon className="w-5 h-5 inline mr-1" /> {t("legal.buttons.back")}
            </button>
            <button
                className="px-4 py-2 rounded action-secondary" 
                onClick={onOpenCookieConsent}
                aria-label={t("cookieConsent.title") || "Open Cookie Consent Modal"}
            >
                <ShieldCheckIcon className="w-5 h-5 inline mr-1" /> {t("cookieConsent.title")}
            </button>
        </div>
      </div>
    </div>
  );
}